import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Stack,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Biotech as BiotechIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { formatCalendarDate } from '../../utils/dateUtils';
import { idexxService, LabRequest, LabRequestStatus } from '../../services/idexxService';

/**
 * Pending strip of in-flight IDEXX lab requests for a horse — analog of
 * ImagingRequestsPanel. Renders DRAFT / CREATED / SUBMITTED / AT_LAB /
 * PARTIAL rows only; COMPLETE rows fall through to the unified bloodwork
 * timeline rendered by the parent.
 *
 * Per-row actions vary by status:
 *   - DRAFT:                    Edit, Submit, Delete
 *   - CREATED:                  Edit, Open in VC+, Cancel
 *   - SUBMITTED/AT_LAB/PARTIAL: Open in VC+, Refresh, Cancel (until AT_LAB)
 *   - FAILED:                   Edit (to retry), Delete
 */

interface LabRequestsPanelProps {
  horseId: string;
  hideAddButton?: boolean;
  hideHeader?: boolean;
  onEdit?: (req: LabRequest) => void;
}

const STATUS_CONFIG: Record<
  LabRequestStatus,
  { color: 'default' | 'primary' | 'info' | 'warning' | 'success' | 'error'; label: string }
> = {
  DRAFT:     { color: 'default', label: 'Draft' },
  CREATED:   { color: 'info',    label: 'Created — finalize in VC+' },
  SUBMITTED: { color: 'primary', label: 'Submitted' },
  AT_LAB:    { color: 'primary', label: 'At the lab' },
  PARTIAL:   { color: 'warning', label: 'Partial results' },
  COMPLETE:  { color: 'success', label: 'Complete' },
  CANCELLED: { color: 'default', label: 'Cancelled' },
  FAILED:    { color: 'error',   label: 'Failed' },
};

// Statuses that should appear in the pending strip. COMPLETE / CANCELLED
// disappear because they live in the main timeline / history.
const PENDING_STATUSES: LabRequestStatus[] = [
  'DRAFT', 'CREATED', 'SUBMITTED', 'AT_LAB', 'PARTIAL', 'FAILED',
];

const LabRequestsPanel: React.FC<LabRequestsPanelProps> = ({ horseId, hideHeader, onEdit }) => {
  const queryClient = useQueryClient();
  const { data: requests = [], isLoading } = useQuery<LabRequest[]>(
    ['idexx-lab-requests', horseId],
    () => idexxService.listLabRequests(horseId),
    { enabled: !!horseId, refetchInterval: 30000 },
  );

  const pending = requests.filter((r) => PENDING_STATUSES.includes(r.status));
  const [busyId, setBusyId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [menuFor, setMenuFor] = useState<{ id: string; el: HTMLElement } | null>(null);

  const refresh = () => queryClient.invalidateQueries(['idexx-lab-requests', horseId]);

  const run = async (id: string, fn: () => Promise<void>) => {
    setBusyId(id);
    setErrors((e) => ({ ...e, [id]: '' }));
    try {
      await fn();
      refresh();
    } catch (err: any) {
      setErrors((e) => ({
        ...e,
        [id]: err.response?.data?.message || 'Action failed.',
      }));
    } finally {
      setBusyId(null);
      setMenuFor(null);
    }
  };

  // Open the reference-lab requisition-form PDF (signed URL from IDEXX, only
  // present once the order is finalized). Separate from `run` because it opens
  // a URL rather than mutating + refetching.
  const openReqForm = async (id: string) => {
    setBusyId(id);
    setErrors((e) => ({ ...e, [id]: '' }));
    try {
      const { url } = await idexxService.getReqFormUrl(id);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setErrors((e) => ({ ...e, [id]: 'Requisition form is not ready yet — finalize the order in VC+ first.' }));
      }
    } catch (err: any) {
      setErrors((e) => ({ ...e, [id]: err.response?.data?.message || 'Could not load the requisition form.' }));
    } finally {
      setBusyId(null);
      setMenuFor(null);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" gap={1} py={2}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Loading lab requests…</Typography>
      </Box>
    );
  }

  if (pending.length === 0) return null;

  return (
    <Box>
      {!hideHeader && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          IDEXX lab requests
        </Typography>
      )}
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {pending.map((r) => {
          const cfg = STATUS_CONFIG[r.status];
          const err = errors[r.id];
          return (
            <Card key={r.id} variant="outlined">
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <BiotechIcon color="primary" sx={{ mt: 0.5 }} />
                  <Box flex={1} minWidth={0}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="body1" fontWeight={500}>
                        {r.modality === 'INHOUSE' ? 'In-house' : 'Reference Lab'} ·{' '}
                        {r.testCodes.length === 0
                          ? 'No tests yet'
                          : `${r.testCodes.length} test${r.testCodes.length === 1 ? '' : 's'}`}
                      </Typography>
                      <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" />
                    </Box>
                    <Typography variant="caption" color="text.secondary" component="div">
                      Vet: {r.veterinarian}
                      {r.specimenCollectionDate && (
                        // CALENDAR (DATE-KINDS.md §1.1 / §2.2). The specimen was
                        // collected on a DAY: idexx-lab-request.service.ts:93,127
                        // anchors a `yyyy-MM-dd` with parseYmdCalendarDay (:431),
                        // which routes through common/calendar-date.parseCalendarDay
                        // — NOON UTC, the one anchor authority. (W5, the divergent
                        // host-local noon this used to have, was closed on
                        // fix/date-foundation-backend; the date-part parse is
                        // indifferent to which noon it was either way.)
                        // A noon anchor does not roll back in a US zone — it rolls
                        // FORWARD past UTC+12 — so what this guards is the general
                        // rule, not one direction: a calendar day is the date PART
                        // and is never re-zoned. The extreme-offset pair in
                        // LabRequestsPanel.test.tsx is the conclusive form of that.
                        <> · Collection: {formatCalendarDate(r.specimenCollectionDate, 'PP')}</>
                      )}
                      {r.idexxOrderId && <> · Order #{r.idexxOrderId}</>}
                    </Typography>
                    {r.lastError && (
                      <Alert severity="warning" sx={{ mt: 1 }}>{r.lastError}</Alert>
                    )}
                    {err && <Alert severity="error" sx={{ mt: 1 }}>{err}</Alert>}
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {busyId === r.id && <CircularProgress size={16} />}
                    {r.uiUrl && (r.status === 'CREATED' || r.status === 'SUBMITTED' || r.status === 'AT_LAB' || r.status === 'PARTIAL') && (
                      <Tooltip title="Open in VC+">
                        <IconButton
                          size="small"
                          onClick={() => window.open(r.uiUrl!, '_blank', 'noopener,noreferrer')}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {r.modality === 'REFLAB' && !!r.idexxOrderId && (r.status === 'SUBMITTED' || r.status === 'AT_LAB' || r.status === 'PARTIAL') && (
                      <Tooltip title="Print requisition form — must ship with the sample">
                        <IconButton size="small" color="primary" onClick={() => openReqForm(r.id)}>
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton size="small" onClick={(e) => setMenuFor({ id: r.id, el: e.currentTarget })}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              </CardContent>
              <Menu
                open={menuFor?.id === r.id}
                anchorEl={menuFor?.el ?? null}
                onClose={() => setMenuFor(null)}
              >
                {(r.status === 'DRAFT' || r.status === 'CREATED' || r.status === 'FAILED') && onEdit && (
                  <MenuItem onClick={() => { onEdit(r); setMenuFor(null); }}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Edit</ListItemText>
                  </MenuItem>
                )}
                {r.status === 'DRAFT' && (
                  <MenuItem onClick={() => run(r.id, async () => { await idexxService.submitLabRequest(r.id); })}>
                    <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Submit to IDEXX</ListItemText>
                  </MenuItem>
                )}
                {(r.status === 'CREATED' || r.status === 'SUBMITTED' || r.status === 'PARTIAL' || r.status === 'AT_LAB') && (
                  <MenuItem onClick={() => run(r.id, async () => { await idexxService.refreshLabRequest(r.id); })}>
                    <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Refresh from IDEXX</ListItemText>
                  </MenuItem>
                )}
                {r.modality === 'REFLAB' && !!r.idexxOrderId && (r.status === 'SUBMITTED' || r.status === 'AT_LAB' || r.status === 'PARTIAL') && (
                  <MenuItem onClick={() => openReqForm(r.id)}>
                    <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Print req form</ListItemText>
                  </MenuItem>
                )}
                {(r.status === 'CREATED' || r.status === 'SUBMITTED') && (
                  <MenuItem onClick={() => run(r.id, async () => { await idexxService.cancelLabRequest(r.id); })}>
                    <ListItemIcon><CancelIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Cancel order</ListItemText>
                  </MenuItem>
                )}
                {(r.status === 'DRAFT' || r.status === 'FAILED') && (
                  <MenuItem onClick={() => run(r.id, async () => { await idexxService.deleteLabRequest(r.id); })}>
                    <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Delete draft</ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};

export default LabRequestsPanel;
